import { shareOrigin } from "@/lib/origin";
import { useState } from "react";
import { Video, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

async function shareInvite(path, friend) {
  const url = `${shareOrigin()}${path}`;
  const text = `${friend}, my coach Adelaida recorded a little message for you:`;
  if (navigator.share) {
    try { await navigator.share({ title: "A message from Adelaida · Xcelio", text, url }); } catch (e) { if (e?.name !== "AbortError") throw e; }
  } else {
    await navigator.clipboard.writeText(`${text} ${url}`);
    toast(`Link copied. Send it to ${friend}.`);
  }
}

// "Send Adelaida to a friend": she records a personal hello for them, shared as a link (with his referral code).
export function InviteFriendDialog({ open, onClose }) {
  const [friend, setFriend] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!friend.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/invites", { friend });
      await shareInvite(data.path, data.friend);
      onClose(); setFriend("");
    } catch (e) {
      toast(e?.response?.data?.detail || "Couldn't make the message. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md z-[95]" overlayClassName="z-[94]" data-testid="invite-dialog">
        <DialogTitle className="font-serif text-2xl">Send Adelaida to a friend</DialogTitle>
        <p className="text-sm text-muted-foreground -mt-1">She'll record a personal hello: "Hey {friend.trim() || "Paul"}, your friend thinks you'd love to experience me as your own personal coach…"</p>
        <Input value={friend} onChange={(e) => setFriend(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} placeholder="Your friend's first name" maxLength={30} data-testid="invite-friend-input" />
        <Button onClick={go} disabled={busy || !friend.trim()} className="rounded-full" data-testid="invite-create-btn">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adelaida is recording…</> : <><Video className="w-4 h-4 mr-2" /> Create & send</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function InviteButton({ className = "", label = "Send Adelaida to a friend", testid = "invite-open-btn" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} className={className || "text-sm text-primary inline-flex items-center gap-1.5 hover:underline"} data-testid={testid}>
        <Video className="w-3.5 h-3.5" /> {label}
      </button>
      <InviteFriendDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default function InviteNudge({ onClose }) {
  return (
    <div className="mb-4 rounded-2xl border border-primary/30 bg-primary/[0.06] px-4 py-3 flex items-center gap-3" data-testid="invite-nudge">
      <p className="text-sm flex-1">Know someone who'd love a coach like this?</p>
      <InviteButton label="Send Adelaida" testid="invite-nudge-btn" />
      <button onClick={onClose} aria-label="Dismiss" className="text-muted-foreground hover:text-foreground" data-testid="invite-nudge-close"><X className="w-4 h-4" /></button>
    </div>
  );
}
